import React, { useState } from 'react';
import { authAPI, profileAPI } from '../services/api';

const LoginModal = ({ isOpen, onClose, onLogin }) => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    closet_name: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isSignup, setIsSignup] = useState(false);
  const [showVerificationScreen, setShowVerificationScreen] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [error, setError] = useState('');


  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!formData.email || !formData.password) {
      setError('Please fill in all required fields');
      return;
    }
    
    if (isSignup && !formData.name) {
      setError('Please enter your name');
      return;
    }
    
    setIsLoading(true);
    
    try {
      if (isSignup) {
        // Register new user
        console.log('Sending registration with formData:', formData);
        await authAPI.register({
          email: formData.email,
          password: formData.password,
          name: formData.name,
          closet_name: formData.closet_name
        });
        
        // Show verification screen instead of alert
        setUserEmail(formData.email);
        setShowVerificationScreen(true);
        
      } else {
        // Login existing user
        console.log('Attempting login with:', { email: formData.email, password: formData.password });
        await onLogin(formData.email, formData.password);
      }
      
      setFormData({ email: '', password: '', name: '', closet_name: '' });
    } catch (error) {
      console.error('Auth error:', error);
      console.error('Error details:', {
        message: error.message,
        stack: error.stack,
        name: error.name
      });
      
      // Handle email verification error specifically
      if (error.message && error.message.includes('verify your email')) {
        setError('✓ Please check your email and click the verification link before logging in.');
        // Show verification screen for unverified users
        setUserEmail(formData.email);
        setShowVerificationScreen(true);
      } else {
        setError(error.message || 'Authentication failed. Please check your credentials.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    try {
      let email = (formData.email || '').trim();
      if (!email) {
        // eslint-disable-next-line no-alert
        email = prompt('Enter your account email to reset your password:') || '';
        email = email.trim();
      }
      if (!email) return;
      await profileAPI.requestPasswordReset(email);
      // eslint-disable-next-line no-alert
      alert('If this email exists, a password reset link has been sent.');
    } catch (_) {
      // eslint-disable-next-line no-alert
      alert('Unable to send reset link right now. Please try again later.');
    }
  };

  const handleSignupClick = (e) => {
    e.preventDefault();
    setIsSignup(!isSignup);
    setError('');
  };

  const handleClose = () => {
    setFormData({ email: '', password: '', name: '', closet_name: '' });
    setError('');
    setIsSignup(false);
    onClose();
  };




  if (!isOpen) return null;

  // Show verification screen if signup was successful
  if (showVerificationScreen) {
    return (
      <div className="modal">
        <div className="modal-content">
          <span className="close" onClick={handleClose}>&times;</span>
          <h2>Check Your Email</h2>
          
          <div style={{textAlign: 'center', padding: '20px 0'}}>
            <div style={{fontSize: '48px', marginBottom: '20px'}}>📧</div>
            <h3 style={{marginBottom: '16px', color: '#28a745'}}>Registration Successful!</h3>
            <p style={{marginBottom: '20px', fontSize: '16px', lineHeight: '1.5'}}>
              We've sent a verification email to <strong>{userEmail}</strong>
            </p>
            <p style={{marginBottom: '30px', color: '#666'}}>
              Please check your email and click the verification link to activate your account.
            </p>
            
            <div style={{display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap'}}>
              <button 
                className="btn btn-primary"
                onClick={async () => {
                  try {
                    await authAPI.resendVerificationEmail({ email: userEmail });
                    alert('Verification email sent! Please check your email.');
                  } catch (error) {
                    alert('Failed to resend verification email. Please try again.');
                  }
                }}
                style={{padding: '12px 24px'}}
              >
                Resend Verification Email
              </button>
              
              <button 
                className="btn btn-secondary"
                onClick={() => {
                  setShowVerificationScreen(false);
                  setIsSignup(false);
                  setFormData({ email: '', password: '', name: '', closet_name: '' });
                }}
                style={{padding: '12px 24px'}}
              >
                Back to Login
              </button>
            </div>
            
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal">
      <div className="modal-content">
        <span className="close" onClick={handleClose}>&times;</span>
        <h2>{isSignup ? 'Sign Up for Ooowap' : 'Login to Ooowap'}</h2>
        
        {error && (
          <div className="error-message" style={{ 
            color: error.startsWith('✓') ? '#22c55e' : '#ff4444', 
            backgroundColor: error.startsWith('✓') ? '#f0fff4' : '#ffe6e6', 
            padding: '12px', 
            borderRadius: '8px', 
            marginBottom: '15px',
            marginTop: '10px',
            textAlign: 'center',
            fontWeight: '500',
            border: error.startsWith('✓') ? '1px solid #22c55e' : '1px solid #ff4444',
            position: 'sticky',
            top: '10px',
            zIndex: 10
          }}>
            {error}
          </div>
        )}
        
        <form className="login-form" onSubmit={handleSubmit}>
          {isSignup && (
            <div className="form-group">
              <label>Full Name *</label>
              <input 
                type="text" 
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="Enter your full name" 
                required 
              />
            </div>
          )}
          
          <div className="form-group">
            <label>Email *</label>
            <input 
              type="email" 
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              placeholder="Enter your email" 
              required 
            />
          </div>
          
          <div className="form-group">
            <label>Password *</label>
            <input 
              type="password" 
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              placeholder="Enter your password" 
              required 
            />
            {!isSignup && (
              <div style={{ marginTop: '6px' }}>
                <button type="button" onClick={handleForgotPassword} style={{ fontSize: '0.9rem', background: 'none', border: 'none', color: 'inherit', textDecoration: 'underline', cursor: 'pointer' }}>Forgot password?</button>
              </div>
            )}
          </div>
          
          {isSignup && (
            <>
              <div className="form-group">
                <label>Closet Name</label>
                <input 
                  type="text" 
                  name="closet_name"
                  value={formData.closet_name}
                  onChange={handleInputChange}
                  placeholder="Enter your closet/store name" 
                />
              </div>
            </>
          )}
          
          <div style={{marginTop: '8px'}}>
            {/* Resend verification button removed - now only appears on verification screen */}
          </div>

          <button 
            type="submit" 
            className="btn btn-primary"
            disabled={isLoading}
          >
            {isLoading ? (isSignup ? 'Creating Account...' : 'Logging in...') : (isSignup ? 'Sign Up' : 'Login')}
          </button>
          
          <p className="login-footer">
            {isSignup ? 'Already have an account?' : "Don't have an account?"} 
            <button type="button" onClick={handleSignupClick} style={{ background: 'none', border: 'none', color: 'inherit', textDecoration: 'underline', cursor: 'pointer' }}>
              {isSignup ? ' Login' : ' Sign up'}
            </button>
          </p>
        </form>
      </div>
    </div>
  );
};

export default LoginModal; 